import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import AuthModal from "../components/auth/AuthModal";
import Logo from "../components/ui/Logo";
import useDarkMode from "../hooks/useDarkMode";
import "../styles/landing.css";

// ── Data ──────────────────────────────────────────────────────────────

const PHONE_FEED = [
  { g: "🍼", m: "<b>Sam</b> logged a 4.0 oz formula feed." },
  { g: "🧷", m: "<b>You</b> logged a wet nappy." },
  { g: "😴", m: "<b>Nana</b> started a nap." },
  { g: "🎯", m: "<b>You</b> added milestone \"First wave\"." },
  { g: "💧", m: "<b>Sam</b> logged 60 ml of water." },
  { g: "📏", m: "<b>You</b> recorded weight 6.4 kg." },
];

const STATS = [
  { count: 14, unit: "+", label: "Tracking categories" },
  { count: 100, unit: "%", label: "Free, forever" },
  { count: 0, unit: "", label: "Ads, ever" },
  { count: 3, unit: "am", label: "Tested & approved" },
];

const FEATURES = [
  { icon: "👥", title: "Shared with your team",  body: "Invite your partner, a grandparent, or a nanny. Everyone sees the same live feed — no more \"did you feed her?\" at 3am." },
  { icon: "✈️", title: "Works offline",           body: "Log on the tube, on a plane, in the garden. Entries sync the moment you're back — nothing is ever lost." },
  { icon: "📊", title: "Reports doctors read",    body: "Export a clean PDF or CSV of any range — feeds, sleep, growth, vaccinations. Ready for your next check-up." },
  { icon: "🔔", title: "Gentle reminders",        body: "Set nap windows, feed intervals, or vaccination dates. Quiet nudges, never anxious pings." },
  { icon: "🔒", title: "Private by default",      body: "Your baby's data is yours. No third-party trackers, no ad networks, no selling. Delete everything any time." },
  { icon: "📵", title: "Free, and staying free",  body: "No subscription, no paywall. Core tracking is free. Just a tool that works." },
];

const TRACKING_CATEGORIES = [
  { g: "🍼", n: "Bottle feeds",    tag: "Feeding",  p: "Log each bottle — ounces, formula vs breast milk, and who gave it.",                       f: ["Amount", "Type", "Time", "Logged by", "Notes"] },
  { g: "🤱", n: "Breastfeeding",   tag: "Feeding",  p: "Start a timer for left or right. The floating pill keeps going even if you close the tab.", f: ["Side", "Duration", "Timer", "Notes"] },
  { g: "🥘", n: "Solid foods",     tag: "Feeding",  p: "Log purées, finger foods, and family meals. Mark reactions to spot a pattern later.",        f: ["Food", "Amount", "Reaction", "Time"] },
  { g: "🍎", n: "Food intros",     tag: "Feeding",  p: "Track first tastes carefully — ideal for allergy watching. Three-day rule built in.",          f: ["Food", "Day 1–3", "Reaction", "Notes"] },
  { g: "🧷", n: "Nappies",         tag: "Health",   p: "Wet, dirty, or both — one tap each. Week view shows hydration at a glance.",                  f: ["Wet", "Dirty", "Both", "Notes"] },
  { g: "😴", n: "Sleep",           tag: "Rest",     p: "Naps and nights separately. Timer keeps running while the app is closed.",                    f: ["Start", "End", "Total", "Where"] },
  { g: "💧", n: "Water",           tag: "Feeding",  p: "For older babies — track sips across the day in ml or oz.",                                  f: ["Amount", "Time", "Notes"] },
  { g: "📏", n: "Growth",          tag: "Health",   p: "Weight, height, head circumference. Overlaid on NHS and CDC curves automatically.",            f: ["Weight", "Height", "Head", "Date"] },
  { g: "🎯", n: "Milestones",      tag: "Memories", p: "First smile, first roll, first words. Attach a photo and the date.",                      f: ["Name", "Date", "Photo", "Note"] },
  { g: "🏥", n: "Doctor visits",   tag: "Health",   p: "Who you saw, what was said, what to do next. Exportable as a PDF.",                           f: ["Date", "Clinician", "Notes", "Follow-ups"] },
  { g: "💉", n: "Vaccinations",    tag: "Health",   p: "NHS and CDC schedules built in. Gentle reminders a week before each dose.",                   f: ["Vaccine", "Date", "Dose", "Reminder"] },
  { g: "📖", n: "Journal",         tag: "Memories", p: "Free-form entries with photos. Everything timestamped automatically.",                         f: ["Date", "Title", "Body", "Photos"] },
];

const DAY_EVENTS = [
  { hr:  6.5, g: "🍼", t: "Morning bottle",    d: "<b>You</b> — 4.0 oz formula · 06:30" },
  { hr:  7.75,g: "🧷", t: "Nappy change",      d: "<b>Sam</b> — wet · 07:45" },
  { hr:  9,   g: "😴", t: "Morning nap",       d: "<b>Sam</b> — 1h 20m · started 09:00" },
  { hr: 11,   g: "🥘", t: "Solid food",        d: "<b>You</b> — sweet potato purée · 11:00" },
  { hr: 12.5, g: "🍼", t: "Midday bottle",     d: "<b>Nana</b> — 3.5 oz breast milk · 12:30" },
  { hr: 14,   g: "😴", t: "Afternoon nap",     d: "<b>Nana</b> — 45 min · started 14:00" },
  { hr: 15.5, g: "🎯", t: "Milestone!",        d: "<b>You</b> — waved bye for the first time · 15:30" },
  { hr: 17,   g: "💧", t: "Water",             d: "<b>You</b> — 60 ml · 17:00" },
  { hr: 18.5, g: "🍼", t: "Evening bottle",    d: "<b>You</b> — 4.0 oz formula · 18:30" },
  { hr: 20,   g: "📖", t: "Journal entry",     d: "<b>You</b> — \"Best day this week\" · 20:00" },
  { hr: 21,   g: "😴", t: "Down for the night",d: "<b>Sam</b> — started 21:00" },
];

const QUOTES = [
  { n: "Priya",  r: "Mum of two · London",       q: "The shared feed saved my sanity. We finally stopped doubling up on feeds at 5am." },
  { n: "Marco",  r: "Dad · Berlin",               q: "Set it up on the plane home from the hospital. Still offline-logging three months later — just works." },
  { n: "Aisha",  r: "New mum · Edinburgh",        q: "Our health visitor asked for a growth chart. One tap, PDF in her inbox. Felt competent for once." },
  { n: "Tom",    r: "Stay-at-home dad · Leeds",   q: "No ads. No paywalled \"pro\" feature. I keep waiting for the catch and there isn't one." },
  { n: "Sofia",  r: "Mum · Madrid",               q: "My mother-in-law started logging feeds when she babysits. I can see them live. Peace of mind." },
  { n: "Jess",   r: "Twin mum · Bristol",         q: "Twin mode where each baby has a separate feed — we actually know who ate what. Game changer." },
];

const STEPS = [
  { title: "Create your baby's profile",  body: "Name, birthday, and (optionally) which theme you'd like the app dressed in. Nothing else needed." },
  { title: "Invite your caregivers",      body: "Share a private invite link with your partner or a grandparent. Revoke access any time." },
  { title: "Log as you go",              body: "Tap a category, add the detail, move on. The timer pill floats at the bottom whenever a nap or feed is running." },
];

// ── Component ─────────────────────────────────────────────────────────

export default function LandingPage() {
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [isDark, toggleDark] = useDarkMode();
  const navigate = useNavigate();

  // Phone feed animation
  const [feedIndex, setFeedIndex] = useState(0);

  // Interactive tracking explorer
  const [activeTrack, setActiveTrack] = useState(0);

  // Day timeline
  const [activeDay, setActiveDay]       = useState(null);
  const [dayDetail, setDayDetail]       = useState("Hover any event on the timeline to see what happened.");

  // Nav scroll state
  const [navScrolled, setNavScrolled]   = useState(false);
  const [activeSection, setActiveSection] = useState(null);

  const openSignup = useCallback(() => { setAuthMode("signup"); setAuthOpen(true); }, []);
  const openLogin  = useCallback(() => { setAuthMode("login");  setAuthOpen(true); }, []);

  // Scroll reveal
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("in"); io.unobserve(e.target); } }),
      { threshold: 0.12 }
    );
    document.querySelectorAll(".reveal").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // Nav scroll shadow + scroll spy
  useEffect(() => {
    const sections = ["features", "tracking", "how", "quotes"].map((id) => ({
      id,
      el: document.getElementById(id),
    }));
    const onScroll = () => {
      setNavScrolled(window.scrollY > 8);
      const y = window.scrollY + 120;
      let active = null;
      sections.forEach((s) => { if (s.el && s.el.offsetTop <= y) active = s.id; });
      setActiveSection(active);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Phone feed cycling
  useEffect(() => {
    const id = setInterval(() => setFeedIndex((i) => (i + 1) % PHONE_FEED.length), 3500);
    return () => clearInterval(id);
  }, []);

  // Animated stat counters
  const statsRef = useRef(null);
  const [statVals, setStatVals] = useState(STATS.map(() => 0));
  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        STATS.forEach((stat, i) => {
          if (!stat.count) return;
          const start = performance.now();
          const tick  = (now) => {
            const p      = Math.min(1, (now - start) / 1200);
            const eased  = 1 - Math.pow(1 - p, 3);
            setStatVals((v) => { const n = [...v]; n[i] = Math.round(stat.count * eased); return n; });
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        });
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Build phone feed rows (last 4, newest first)
  const feedRows = Array.from({ length: 4 }, (_, i) => ({
    ...PHONE_FEED[(feedIndex + i) % PHONE_FEED.length],
    isNew: i === 0,
    time: i === 0 ? "Just now" : `${i * 15 + 12} min ago`,
  }));

  // Timeline event handler
  const handleDayEvent = (ev, idx) => {
    setActiveDay(idx);
    setDayDetail(`<b>${ev.t}</b> — ${ev.d}`);
  };

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="landing-root">

      {/* ── NAV ────────────────────────────────────────────────────── */}
      <nav className={`landing-nav${navScrolled ? " scrolled" : ""}`}>
        <div className="landing-container landing-nav-inner">
          <button
            className="landing-nav-brand"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            style={{ background: "none", border: "none" }}
          >
            <Logo variant="full" size={32} />
          </button>

          <div className="landing-nav-links">
            {[["features","Features"],["tracking","What's tracked"],["how","How it works"],["quotes","From parents"]].map(([id, label]) => (
              <a
                key={id}
                href={`#${id}`}
                className={activeSection === id ? "active" : ""}
                onClick={(e) => { e.preventDefault(); scrollTo(id); }}
              >
                {label}
              </a>
            ))}
          </div>

          <div className="landing-nav-actions">
            <button className="landing-nav-theme" onClick={toggleDark} aria-label="Toggle dark mode">
              {isDark ? "☀️" : "🌙"}
            </button>
            <a href="/docs" className="btn btn-sm btn-outline-secondary">Docs</a>
            <button className="btn btn-sm btn-outline-secondary" onClick={openLogin}>Log in</button>
            <button className="landing-nav-cta" onClick={openSignup}>Sign up free</button>
          </div>
        </div>
      </nav>

      {/* ── HERO ───────────────────────────────────────────────────── */}
      <section className="landing-hero">
        <div className="landing-hero-shapes" aria-hidden="true">
          <div className="landing-hero-shape s1" />
          <div className="landing-hero-shape s2" />
          <div className="landing-hero-shape s3" />
        </div>

        <div className="landing-container">
          <div className="landing-hero-inner">

            {/* Left: copy */}
            <div>
              <div className="landing-hero-eyebrow">
                <span className="landing-live-dot" aria-hidden="true" />
                Live with 2 caregivers right now
              </div>
              <h1 className="landing-hero-heading">
                Every feed, nap, and{" "}
                <span className="landing-hero-accent">milestone</span>
                {" "}— in one place.
              </h1>
              <p className="landing-hero-sub">
                Baby Tracker gives parents and caregivers a shared, real-time
                record of their baby's day. No paper, no guesswork, no subscription.
              </p>
              <div className="landing-hero-ctas">
                <button className="landing-btn-pill" onClick={openSignup}>
                  Get started free <span className="arr" aria-hidden="true">→</span>
                </button>
                <button className="landing-btn-pill-outline" onClick={() => scrollTo("how")}>
                  See how it works
                </button>
              </div>
              <div className="landing-hero-trust">
                <span className="landing-hero-trust-pill">✓ Free to use</span>
                <span className="landing-hero-trust-pill">✈️ Works offline</span>
                <span className="landing-hero-trust-pill">🔒 Your data, private</span>
                <span className="landing-hero-trust-pill">📵 No ads, ever</span>
              </div>
            </div>

            {/* Right: animated phone */}
            <div className="landing-phone-wrap">
              <div className="landing-phone" aria-hidden="true">
                <div className="landing-phone-screen">
                  <div className="landing-phone-top">
                    <div className="landing-phone-who">
                      <div className="landing-phone-who-avatar">E</div>
                      Emma · 7 mo
                    </div>
                    <h3>Today's dashboard</h3>
                    <div className="sub">Updated just now</div>
                  </div>
                  <div className="landing-phone-stats">
                    <div className="landing-phone-stat">
                      <div className="val">6</div>
                      <div className="lab">🍼 Feeds</div>
                    </div>
                    <div className="landing-phone-stat">
                      <div className="val">3h 45m</div>
                      <div className="lab">😴 Sleep</div>
                    </div>
                    <div className="landing-phone-stat">
                      <div className="val">5</div>
                      <div className="lab">🧷 Nappies</div>
                    </div>
                  </div>
                  <div className="landing-phone-feed">
                    {feedRows.map((row, i) => (
                      <div key={i} className={`landing-phone-feed-row${row.isNew ? " new" : ""}`}>
                        <span className="gl">{row.g}</span>
                        <div>
                          {/* eslint-disable-next-line react/no-danger */}
                          <div className="msg" dangerouslySetInnerHTML={{ __html: row.m }} />
                          <div className="meta">{row.time}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="landing-phone-fab" aria-label="Quick log">+</div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── STATS STRIP ─────────────────────────────────────────────── */}
      <section className="landing-section" style={{ paddingTop: "3rem", paddingBottom: "3rem" }}>
        <div className="landing-container">
          <div className="landing-stats-strip reveal" ref={statsRef}>
            {STATS.map((s, i) => (
              <div key={s.label} className="landing-stat-big">
                <div className="landing-stat-num">
                  <span>{statVals[i]}</span>
                  {s.unit && <span className="unit">{s.unit}</span>}
                </div>
                <div className="lab">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES ────────────────────────────────────────────────── */}
      <section id="features" className="landing-section landing-section-alt">
        <div className="landing-container">
          <div className="landing-sec-head reveal">
            <div className="landing-eyebrow">What's included</div>
            <h2 className="landing-section-heading">Built for the way families actually parent.</h2>
            <p className="landing-section-sub">Every caregiver on the same page, in real time. No more repeating yourself at 3am.</p>
          </div>
          <div className="landing-features-grid">
            {FEATURES.map((f) => (
              <div key={f.title} className="landing-feature-card reveal">
                <div className="landing-feature-icon-wrap">{f.icon}</div>
                <h4 className="landing-feature-title">{f.title}</h4>
                <p className="landing-feature-body">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TRACKING EXPLORER ───────────────────────────────────────── */}
      <section id="tracking" className="landing-section">
        <div className="landing-container">
          <div className="landing-sec-head reveal">
            <div className="landing-eyebrow">14+ categories</div>
            <h2 className="landing-section-heading">Track everything, overwhelm no one.</h2>
            <p className="landing-section-sub">Click a category to see what's inside. Hide the rest in settings — they'll be there when you need them.</p>
          </div>
          <div className="landing-tracker reveal">
            <div className="landing-tracker-grid">
              {TRACKING_CATEGORIES.map((t, i) => (
                <button
                  key={t.n}
                  className={`landing-tracker-tile${activeTrack === i ? " active" : ""}`}
                  onClick={() => setActiveTrack(i)}
                  onMouseEnter={() => setActiveTrack(i)}
                >
                  <div className="g">{t.g}</div>
                  <div className="n">{t.n}</div>
                </button>
              ))}
            </div>
            <div className="landing-tracker-detail">
              <div className="big-emoji">{TRACKING_CATEGORIES[activeTrack].g}</div>
              <div className="landing-tracker-tag">{TRACKING_CATEGORIES[activeTrack].tag}</div>
              <h3>{TRACKING_CATEGORIES[activeTrack].n}</h3>
              <p>{TRACKING_CATEGORIES[activeTrack].p}</p>
              <div className="landing-tracker-fields">
                {TRACKING_CATEGORIES[activeTrack].f.map((field) => (
                  <span key={field} className="landing-tracker-field">{field}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── TIMELINE ────────────────────────────────────────────────── */}
      <section className="landing-section landing-section-alt">
        <div className="landing-container">
          <div className="landing-sec-head reveal">
            <div className="landing-eyebrow">A typical Tuesday</div>
            <h2 className="landing-section-heading">See Emma's day — hover any moment.</h2>
            <p className="landing-section-sub">Every point is a logged event. This is how the timeline looks inside the app.</p>
          </div>
          <div className="landing-day-card reveal">
            <div className="landing-day-head">
              <div className="t">📅 Tuesday, 12 April</div>
              <div className="sub">11 events · shared with Sam &amp; Nana</div>
            </div>
            <div className="landing-day-track" role="list" aria-label="Day timeline">
              {DAY_EVENTS.map((ev, i) => {
                const pct = ((ev.hr - 6) / 15) * 100;
                return (
                  <button
                    key={i}
                    className={`landing-day-event${activeDay === i ? " active" : ""}`}
                    style={{ left: `${pct}%` }}
                    title={ev.t}
                    role="listitem"
                    onMouseEnter={() => handleDayEvent(ev, i)}
                    onClick={() => handleDayEvent(ev, i)}
                    aria-label={ev.t}
                  >
                    <span>{ev.g}</span>
                  </button>
                );
              })}
            </div>
            <div className="landing-day-axis" aria-hidden="true">
              <span>6am</span><span>9am</span><span>12pm</span><span>3pm</span><span>6pm</span><span>9pm</span>
            </div>
            {/* eslint-disable-next-line react/no-danger */}
            <div className="landing-day-detail" dangerouslySetInnerHTML={{ __html: dayDetail }} />
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ────────────────────────────────────────────── */}
      <section id="how" className="landing-section">
        <div className="landing-container">
          <div className="landing-sec-head reveal">
            <div className="landing-eyebrow">Get started</div>
            <h2 className="landing-section-heading">Set up in under two minutes.</h2>
            <p className="landing-section-sub">No credit card, no onboarding survey — just your baby's name.</p>
          </div>
          <div className="landing-steps-grid">
            {STEPS.map((s) => (
              <div key={s.title} className="landing-step reveal">
                <h4>{s.title}</h4>
                <p>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ────────────────────────────────────────────── */}
      <section id="quotes" className="landing-section landing-section-alt">
        <div className="landing-container">
          <div className="landing-sec-head reveal">
            <div className="landing-eyebrow">From the parents</div>
            <h2 className="landing-section-heading">Words from the 3am trenches.</h2>
          </div>
        </div>
        <div className="landing-quotes-track reveal">
          <div className="landing-quotes-row">
            {/* Duplicate quotes for seamless marquee loop */}
            {[...QUOTES, ...QUOTES].map((q, i) => (
              <div key={i} className="landing-quote">
                <p>"{q.q}"</p>
                <div className="landing-quote-who">
                  <div className="landing-quote-avatar">{q.n[0]}</div>
                  <div>
                    <div className="landing-quote-name">{q.n}</div>
                    <div className="landing-quote-role">{q.r}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA BAND ────────────────────────────────────────────────── */}
      <section className="landing-section">
        <div className="landing-container">
          <div className="landing-cta-band reveal">
            <h2>Ready to stop guessing?</h2>
            <p>Set up your baby's profile and invite a caregiver in under two minutes. Free forever.</p>
            <div className="landing-cta-band-btns">
              <button className="landing-cta-white" onClick={openSignup}>Sign up free →</button>
              <a href="/docs" className="landing-cta-ghost">Read the docs</a>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ──────────────────────────────────────────────────── */}
      <footer className="landing-footer">
        <div className="landing-container">
          <div className="landing-footer-inner">
            <Logo variant="full" size={28} />
            <nav className="landing-footer-links">
              <a href="/docs">Help</a>
              <a href="/roadmap">Roadmap</a>
              <a href="/privacy">Privacy</a>
              <a href="/terms">Terms</a>
              <button className="landing-footer-btn-link" onClick={openLogin}>Log in</button>
              <button className="landing-footer-btn-link" onClick={openSignup}>Sign up</button>
            </nav>
          </div>
          <div className="landing-footer-copy">
            Made with care by parents, for parents. © 2026 Baby Tracker.
          </div>
        </div>
      </footer>

      {/* ── AUTH MODAL ──────────────────────────────────────────────── */}
      {authOpen && (
        <AuthModal
          initialMode={authMode}
          onClose={() => setAuthOpen(false)}
          onSuccess={() => { setAuthOpen(false); navigate("/app"); }}
        />
      )}
    </div>
  );
}
